import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { createProduct, deleteProduct, deleteProductImage, enableProductVariants, getProductSuggestions, getProducts, updateProduct, uploadProductImage } from "../api/products";
import { createInventory } from "../api/inventory";
import { getApiError } from "../api/errors";
import PageHeader from "../components/PageHeader";
import CatalogFilters from "../components/CatalogFilters";
import ProductBarcodeDialog from "../components/ProductBarcodeDialog";
import ProductImageField from "../components/ProductImageField";
import ProductVariantManager from "../components/ProductVariantManager";
import { useAIPageContext } from "../context/AIAssistantContext";
import { EmptyPanel, LoadingPanel } from "../components/PageState";
import { useAuth } from "../context/AuthContext";
import { useConnectivity } from "../context/ConnectivityContext";
import { getOfflineProductsPage } from "../offline/catalog";
import useDebouncedValue from "../hooks/useDebouncedValue";
import MainLayout from "../layouts/MainLayout";
import { formatMoney, titleCase } from "../utils/format";

const PAGE_SIZE = 24;
const CACHE_TTL_MS = 30_000;
const MAX_CACHE_ENTRIES = 120;
const pageCache = new Map();
const emptyForm = {
  name: "",
  brand: "",
  category: "",
  price: "",
  wholesale_price: "",
  wholesale_min_quantity: "",
  cost_price: "",
  minimum_price: "",
  description: "",
  barcode: "",
  has_variants: false,
  track_inventory: true,
  initial_stock: "0",
  stock_minimum: "5",
};
const MONEY_FIELDS = new Set(["price", "wholesale_price", "cost_price", "minimum_price"]);

function normalizeMoneyInput(value) {
  const normalized = String(value ?? "")
    .replace(/,/g, "")
    .replace(/[^0-9.]/g, "");
  const [whole = "", ...fractionParts] = normalized.split(".");
  const fraction = fractionParts.join("");

  if (fractionParts.length === 0) return whole;
  return `${whole}.${fraction}`;
}

function cacheKey(userId, workspaceId, query, brand, category, page) {
  return `${userId || "anonymous"}|${workspaceId || "none"}|${query || ""}|${brand || ""}|${category || ""}|${page}|${PAGE_SIZE}`;
}

function invalidateWorkspaceCache(userId, workspaceId) {
  const prefix = `${userId || "anonymous"}|${workspaceId || "none"}|`;
  for (const key of pageCache.keys()) {
    if (key.startsWith(prefix)) pageCache.delete(key);
  }
}

function cachePage(key, value) {
  pageCache.delete(key);
  pageCache.set(key, value);
  while (pageCache.size > MAX_CACHE_ENTRIES) {
    pageCache.delete(pageCache.keys().next().value);
  }
}

export default function Products() {
  const { t } = useTranslation();
  const { canManage, selectedStore, selectedWorkspace, user } = useAuth();
  const { isOnline } = useConnectivity();
  const offlineReadOnly = !isOnline;
  const canManageOnline = canManage && isOnline;
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [snapshotMeta, setSnapshotMeta] = useState(null);
  const [query, setQuery] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [barcodeProduct, setBarcodeProduct] = useState(null);
  const [variantProduct, setVariantProduct] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [brandSuggestions, setBrandSuggestions] = useState([]);
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [productImageFile, setProductImageFile] = useState(null);
  const [removeProductImage, setRemoveProductImage] = useState(false);
  const focusAIAction = useAIPageContext(variantProduct ? {
    version: 1, page_id: "products.edit", entity_type: "product", entity_public_id: variantProduct.public_id, section: "variants",
  } : dialogOpen ? {
    version: 1, page_id: editingProduct ? "products.edit" : "products.create",
    ...(editingProduct ? { entity_type: "product", entity_public_id: editingProduct.public_id } : {}), section: "details",
  } : { version: 1, page_id: "products.list" });
  const productsRef = useRef(products);
  const debouncedQuery = useDebouncedValue(query.trim(), 350);

  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  useEffect(() => {
    if (!dialogOpen || !isOnline) return undefined;
    let live = true;
    const timer = setTimeout(() => {
      Promise.all([
        getProductSuggestions("brand", form.brand),
        getProductSuggestions("category", form.category),
      ]).then(([brands, categories]) => {
        if (!live) return;
        setBrandSuggestions(brands.data || []);
        setCategorySuggestions(categories.data || []);
      }).catch(() => {
        if (live) { setBrandSuggestions([]); setCategorySuggestions([]); }
      });
    }, 200);
    return () => { live = false; clearTimeout(timer); };
  }, [dialogOpen, form.brand, form.category, isOnline, selectedWorkspace?.public_id]);

  useEffect(() => {
    if (isOnline) return;
    setDialogOpen(false);
    setEditingProduct(null);
    setBarcodeProduct(null);
    setVariantProduct(null);
  }, [isOnline]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, brandFilter, categoryFilter, selectedStore?.public_id, selectedWorkspace?.public_id, user?.public_id]);

  useEffect(() => { setBrandFilter(""); setCategoryFilter(""); }, [selectedWorkspace?.public_id]);

  const load = useCallback(async ({ force = false, signal } = {}) => {
    if (!isOnline) {
      if (productsRef.current.length) setRefreshing(true);
      else setLoading(true);
      setError("");
      try {
        const local = await getOfflineProductsPage({
          workspacePublicId: selectedWorkspace?.public_id,
          storePublicId: selectedStore?.public_id,
          query: debouncedQuery,
          brand: brandFilter,
          category: categoryFilter,
          page,
          pageSize: PAGE_SIZE,
        });
        setProducts(local.items);
        setTotal(local.total);
        setSnapshotMeta(local.metadata);
        if (!local.metadata) {
          setError(t("products.snapshotMissing"));
        }
      } catch (storageError) {
        setProducts([]);
        setTotal(0);
        setSnapshotMeta(null);
        setError(storageError?.message || t("products.snapshotError"));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
      return;
    }

    setSnapshotMeta(null);
    const key = cacheKey(user?.public_id, selectedWorkspace?.public_id, debouncedQuery, brandFilter, categoryFilter, page);
    const cached = pageCache.get(key);
    const isFresh = cached && Date.now() - cached.cachedAt < CACHE_TTL_MS;

    if (!force && isFresh) {
      setProducts(cached.items);
      setTotal(cached.total);
      setLoading(false);
      return;
    }

    if (productsRef.current.length) setRefreshing(true);
    else setLoading(true);
    setError("");

    try {
      const response = await getProducts(
        {
          query: debouncedQuery || undefined,
          brand: brandFilter || undefined,
          category: categoryFilter || undefined,
          page,
          page_size: PAGE_SIZE,
        },
        { signal },
      );
      const payload = response.data || {};
      const nextItems = Array.isArray(payload.items) ? payload.items : [];
      const nextTotal = Number(payload.total || 0);

      setProducts(nextItems);
      setTotal(nextTotal);
      cachePage(key, {
        items: nextItems,
        total: nextTotal,
        cachedAt: Date.now(),
      });
    } catch (requestError) {
      if (requestError?.code === "ERR_CANCELED") return;
      setError(getApiError(requestError, t("products.loadError")));
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [
    debouncedQuery,
    brandFilter,
    categoryFilter,
    isOnline,
    page,
    selectedStore?.public_id,
    selectedWorkspace?.public_id,
    user?.public_id,
    t,
  ]);

  useEffect(() => {
    const controller = new AbortController();
    load({ signal: controller.signal });
    return () => controller.abort();
  }, [load]);

  const saveProduct = async () => {
    if (!isOnline) {
      setError(t("products.locked"));
      return;
    }
    const price = Number(form.price);
    const costPrice = form.cost_price === "" ? null : Number(form.cost_price);
    const minimumPrice = form.minimum_price === "" ? null : Number(form.minimum_price);
    const wholesalePrice = form.wholesale_price === "" ? null : Number(form.wholesale_price);
    const wholesaleMinimum = form.wholesale_min_quantity === "" ? null : Number(form.wholesale_min_quantity);
    const initialStock = Number(form.initial_stock);
    const stockMinimum = Number(form.stock_minimum);
    if (!form.name.trim() || !form.brand.trim() || !form.category.trim()) {
      setError(t("products.required"));
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      setError(t("products.invalidPrice"));
      return;
    }
    if (costPrice !== null && (!Number.isFinite(costPrice) || costPrice < 0)) {
      setError(t("products.invalidCost"));
      return;
    }
    if (minimumPrice !== null && (!Number.isFinite(minimumPrice) || minimumPrice < 0 || minimumPrice > price)) {
      setError(t("products.invalidMinimum"));
      return;
    }
    if (wholesalePrice !== null && (!Number.isFinite(wholesalePrice) || wholesalePrice < 0)) {
      setError(t("products.invalidPrice"));
      return;
    }
    if (wholesaleMinimum !== null && (!Number.isInteger(wholesaleMinimum) || wholesaleMinimum <= 0)) {
      setError(t("products.invalidWholesaleMinimum"));
      return;
    }
    if (!editingProduct && !form.has_variants && form.track_inventory) {
      if (!Number.isInteger(initialStock) || initialStock < 0 || !Number.isInteger(stockMinimum) || stockMinimum < 0) {
        setError(t("products.invalidStock"));
        return;
      }
      if (!selectedStore?.public_id) {
        setError(t("products.selectStore"));
        return;
      }
    }

    setSaving(true);
    setError("");

    const payload = {
      name: form.name.trim(),
      brand: form.brand.trim(),
      category: form.category.trim(),
      price,
      cost_price: costPrice,
      minimum_price: minimumPrice,
      wholesale_price: wholesalePrice,
      wholesale_min_quantity: wholesaleMinimum,
      description: form.description.trim() || null,
      track_inventory: form.track_inventory,
    };
    if (!editingProduct) {
      payload.barcode = form.barcode.trim() || null;
    }

    let savedPublicId = "";
    let savedProduct = null;
    try {
      const productResponse = editingProduct
        ? await updateProduct(editingProduct.public_id, payload)
        : await createProduct(payload);
      savedProduct = productResponse?.data || editingProduct;
      savedPublicId = savedProduct?.public_id || "";
      if (!savedPublicId) throw new Error(t("products.missingId"));
    } catch (requestError) {
      setError(getApiError(
        requestError,
        t(editingProduct ? "products.updateError" : "products.createError"),
      ));
      setSaving(false);
      return;
    }

    if (!editingProduct && !form.has_variants && form.track_inventory) {
      try {
        await createInventory({
          product_public_id: savedPublicId,
          product_variant_public_id: null,
          store_public_id: selectedStore.public_id,
          quantity: initialStock,
          minimum_stock: stockMinimum,
          location: selectedStore.name || "Main Store",
          source: "manual",
        });
      } catch (requestError) {
        setForm(emptyForm);
        setProductImageFile(null);
        setRemoveProductImage(false);
        setDialogOpen(false);
        invalidateWorkspaceCache(user?.public_id, selectedWorkspace?.public_id);
        setPage(1);
        await load({ force: true });
        const detail = getApiError(requestError, t("products.inventoryError"));
        setError(t("products.partialInventory", { detail }));
        setSaving(false);
        return;
      }
    }

    try {
      if (removeProductImage && editingProduct?.image && !productImageFile) {
        await deleteProductImage(savedPublicId);
      }
      if (productImageFile) {
        await uploadProductImage(savedPublicId, productImageFile);
      }
      if (form.has_variants && !editingProduct?.has_variants) {
        const variantResponse = await enableProductVariants(savedPublicId);
        savedProduct = { ...savedProduct, has_variants: variantResponse.data?.has_variants ?? true };
      }
    } catch (requestError) {
      setForm(emptyForm);
      setProductImageFile(null);
      setRemoveProductImage(false);
      setDialogOpen(false);
      invalidateWorkspaceCache(user?.public_id, selectedWorkspace?.public_id);
      setPage(1);
      await load({ force: true });
      setError(getApiError(
        requestError,
        t("products.followUpError"),
      ));
      setSaving(false);
      return;
    }

    setForm(emptyForm);
    setProductImageFile(null);
    setRemoveProductImage(false);
    setDialogOpen(false);
    setSuccess(t(editingProduct ? "products.updated" : "products.created"));
    invalidateWorkspaceCache(user?.public_id, selectedWorkspace?.public_id);
    setPage(1);
    await load({ force: true });
    if (form.has_variants && !editingProduct?.has_variants && savedProduct) setVariantProduct(savedProduct);
    setSaving(false);
  };

  const remove = async (product) => {
    if (!isOnline) {
      setError(t("products.locked"));
      return;
    }
    if (!window.confirm(t("products.deleteConfirm", { brand: product.brand, name: product.name }))) return;
    setDeletingId(product.public_id);
    setError("");
    try {
      await deleteProduct(product.public_id);
      invalidateWorkspaceCache(user?.public_id, selectedWorkspace?.public_id);
      setSuccess(t("products.deleted"));
      if (products.length === 1 && page > 1) setPage((current) => current - 1);
      else await load({ force: true });
    } catch (requestError) {
      setError(getApiError(requestError, t("products.deleteError")));
    } finally {
      setDeletingId("");
    }
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setForm({
      name: product.name || "",
      brand: product.brand || "",
      category: product.category || "",
      price: String(product.price ?? ""),
      cost_price: product.cost_price === null || product.cost_price === undefined ? "" : String(product.cost_price),
      minimum_price: product.minimum_price === null || product.minimum_price === undefined ? "" : String(product.minimum_price),
      wholesale_price: product.wholesale_price == null ? "" : String(product.wholesale_price),
      wholesale_min_quantity: product.wholesale_min_quantity == null ? "" : String(product.wholesale_min_quantity),
      description: product.description || "",
      barcode: "",
      has_variants: Boolean(product.has_variants),
      track_inventory: product.track_inventory !== false,
      initial_stock: "0",
      stock_minimum: "5",
    });
    setProductImageFile(null);
    setRemoveProductImage(false);
    setError("");
    setDialogOpen(true);
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <MainLayout>
      <PageHeader
        eyebrow={t("products.eyebrow")}
        title={t("nav.products")}
        description={t("products.subtitle")}
        actions={(
          <>
            <Chip
              label={t("catalog.productsCount", { count: total.toLocaleString("en-US") })}
              variant="outlined"
              sx={{
                height: 42,
                borderColor: "#dbe3ef",
                bgcolor: "#fff",
                fontWeight: 850,
                px: 0.5,
              }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              onClick={() => load({ force: true })}
              disabled={refreshing}
              sx={{
                borderColor: "#cbd5e1",
                color: "text.primary",
                bgcolor: "#fff",
                "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
              }}
            >
              {t(refreshing ? "catalog.refreshing" : "catalog.refresh")}
            </Button>
            {canManageOnline && (
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={() => {
                  setError("");
                  setEditingProduct(null);
                  setForm(emptyForm);
                  setProductImageFile(null);
                  setRemoveProductImage(false);
                  setDialogOpen(true);
                }}
                sx={{ px: 2.25 }}
              >
                {t("products.add")}
              </Button>
            )}
          </>
        )}
      />

      {offlineReadOnly && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("products.offlineNotice", { timestamp: snapshotMeta?.cached_at ? ` (${new Date(snapshotMeta.cached_at).toLocaleString()})` : "" })}
        </Alert>
      )}
      {!offlineReadOnly && !canManage && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("products.readOnly")}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>{success}</Alert>}

      <Box
        sx={{
          mb: 2.5,
          p: { xs: 1.5, sm: 2 },
          border: "1px solid #e2e8f0",
          borderRadius: 4,
          bgcolor: "#fff",
          boxShadow: "0 10px 30px rgba(15, 23, 42, 0.035)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          gap={1.5}
          sx={{ alignItems: { sm: "center" } }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder={t("products.search")}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ maxWidth: 760 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: { sm: "nowrap" } }}>
            {refreshing ? t("catalog.updating") : t("catalog.shown", { count: Math.min(products.length, PAGE_SIZE) })}
          </Typography>
        </Stack>
        <CatalogFilters brand={brandFilter} category={categoryFilter} onBrandChange={setBrandFilter}
          onCategoryChange={setCategoryFilter} isOnline={isOnline}
          workspacePublicId={selectedWorkspace?.public_id} storePublicId={selectedStore?.public_id} />
      </Box>

      {loading ? (
        <LoadingPanel label={t("products.loading")} />
      ) : products.length === 0 ? (
        <EmptyPanel
          icon="📦"
          title={t(debouncedQuery ? "products.noMatch" : "products.empty")}
          description={t(debouncedQuery ? "products.noMatchHelp" : "products.emptyHelp")}
        />
      ) : (
        <>
          <TableContainer
            sx={{
              display: { xs: "none", lg: "block" },
              bgcolor: "#fff",
              borderRadius: 4,
              border: "1px solid #e2e8f0",
              boxShadow: "0 14px 34px rgba(15, 23, 42, 0.045)",
              overflow: "hidden",
            }}
          >
            <Table sx={{ minWidth: 1120 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ pl: 2.5 }}>{t("common.product")}</TableCell>
                  <TableCell>{t("catalog.brand")}</TableCell>
                  <TableCell>{t("catalog.category")}</TableCell>
                  <TableCell align="right">{t("common.price")}</TableCell>
                  {canManageOnline && <TableCell align="right">{t("catalog.catalogCost")}</TableCell>}
                  <TableCell align="right">{t("catalog.minimum")}</TableCell>
                  <TableCell>{t("catalog.source")}</TableCell>
                  {canManageOnline && <TableCell align="right" sx={{ pr: 2.5 }}>{t("common.actions")}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => (
                  <TableRow
                    key={product.public_id}
                    hover
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "&:hover": { bgcolor: "#f8fbff" },
                    }}
                  >
                    <TableCell sx={{ py: 1.6, pl: 2.5, minWidth: 300 }}>
                      <ButtonBase
                        aria-label={t("products.editNamed", { name: product.name })}
                        disabled={!canManageOnline}
                        onClick={() => openEditProduct(product)}
                        sx={{
                          width: "100%",
                          justifyContent: "flex-start",
                          borderRadius: 2.5,
                          p: 0.5,
                          mx: -0.5,
                          textAlign: "start",
                          "&:hover": { bgcolor: "action.hover" },
                          "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
                        }}
                      >
                      <Stack direction="row" spacing={1.4} sx={{ alignItems: "center", width: "100%" }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            flex: "0 0 auto",
                            borderRadius: 2.5,
                            display: "grid",
                            placeItems: "center",
                            color: "primary.main",
                            background: "linear-gradient(135deg, #eff6ff 0%, #eef2ff 100%)",
                            border: "1px solid #dbeafe",
                          }}
                        >
                          <Inventory2RoundedIcon fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={850} sx={{ lineHeight: 1.3 }}>{product.name}</Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.3, maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          >
                            {offlineReadOnly
                              ? (product.external_id ? t("products.externalId", { id: product.external_id }) : t("products.offlineIdentity"))
                              : (product.description || (product.external_id ? t("products.externalId", { id: product.external_id }) : t("products.noDescription")))}
                          </Typography>
                        </Box>
                      </Stack>
                      </ButtonBase>
                    </TableCell>
                    <TableCell><Typography variant="body2" fontWeight={700}>{product.brand}</Typography></TableCell>
                    <TableCell><Chip label={product.category} size="small" variant="outlined" /></TableCell>
                    <TableCell align="right"><Typography fontWeight={900}>{formatMoney(product.price)}</Typography></TableCell>
                    {canManageOnline && (
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary" fontWeight={700}>
                          {product.cost_price !== null && product.cost_price !== undefined ? formatMoney(product.cost_price) : "—"}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary" fontWeight={700}>
                        {product.minimum_price !== null && product.minimum_price !== undefined ? formatMoney(product.minimum_price) : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                        <Chip label={titleCase(product.source)} size="small" sx={{ bgcolor: "#f8fafc" }} />
                        {product.image && (
                          <Chip icon={<ImageRoundedIcon />} label={t("catalog.photo")} size="small" color="primary" variant="outlined" />
                        )}
                        {product.has_variants && <Chip label={t("catalog.variants")} size="small" color="secondary" variant="outlined" />}
                        {product.track_inventory === false && <Chip label={t("catalog.notTracked")} size="small" variant="outlined" />}
                      </Stack>
                    </TableCell>
                    {canManageOnline && (
                      <TableCell align="right" sx={{ pr: 2.5, whiteSpace: "nowrap" }}>
                        <Stack direction="row" spacing={0.75} sx={{ justifyContent: "flex-end" }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<QrCodeScannerRoundedIcon fontSize="small" />}
                            onClick={() => product.has_variants ? setVariantProduct(product) : setBarcodeProduct(product)}
                            sx={{ minHeight: 34, px: 1.25, borderColor: "#bfdbfe", color: "primary.main" }}
                          >
                            {t(product.has_variants ? "catalog.variants" : "catalog.barcodes")}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditRoundedIcon fontSize="small" />}
                            onClick={() => openEditProduct(product)}
                            sx={{ minHeight: 34, px: 1.25, borderColor: "#cbd5e1", color: "text.primary" }}
                          >
                            {t("common.edit")}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            startIcon={<DeleteOutlineRoundedIcon fontSize="small" />}
                            disabled={deletingId === product.public_id}
                            onClick={() => remove(product)}
                            sx={{ minHeight: 34, px: 1.25 }}
                          >
                            {t(deletingId === product.public_id ? "products.deleting" : "common.delete")}
                          </Button>
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container spacing={1.75} sx={{ display: { xs: "flex", lg: "none" } }}>
            {products.map((product) => (
              <Grid item xs={12} sm={6} key={product.public_id}>
                <Card sx={{ height: "100%", borderRadius: 3.5, boxShadow: "0 10px 28px rgba(15, 23, 42, 0.045)" }}>
                  <CardContent sx={{ p: 2.25 }}>
                    <Stack
                      direction="row"
                      gap={1.5}
                      sx={{ justifyContent: "space-between", alignItems: "flex-start" }}
                    >
                      <ButtonBase
                        aria-label={t("products.editNamed", { name: product.name })}
                        disabled={!canManageOnline}
                        onClick={() => openEditProduct(product)}
                        sx={{
                          minWidth: 0,
                          borderRadius: 2,
                          p: 0.5,
                          mx: -0.5,
                          textAlign: "start",
                          justifyContent: "flex-start",
                          "&:hover": { bgcolor: "action.hover" },
                          "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.25 }}>{product.name}</Typography>
                          <Typography variant="body2" color="text.secondary" fontWeight={700} sx={{ mt: 0.35 }}>{product.brand}</Typography>
                        </Box>
                      </ButtonBase>
                      <Typography fontWeight={900} color="primary.main" sx={{ whiteSpace: "nowrap" }}>{formatMoney(product.price)}</Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      useFlexGap
                      sx={{ mt: 1.5, flexWrap: "wrap" }}
                    >
                      <Chip label={product.category} size="small" variant="outlined" />
                      <Chip label={titleCase(product.source)} size="small" sx={{ bgcolor: "#f8fafc" }} />
                      {product.image && <Chip icon={<ImageRoundedIcon />} label={t("catalog.photo")} size="small" color="primary" variant="outlined" />}
                      {product.has_variants && <Chip label={t("catalog.variants")} size="small" color="secondary" variant="outlined" />}
                      {product.track_inventory === false && <Chip label={t("catalog.notTracked")} size="small" variant="outlined" />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.6 }}>
                      {offlineReadOnly ? t("products.descriptionOnline") : (product.description || t("products.noDescriptionProvided"))}
                    </Typography>
                    {canManageOnline && (
                      <Stack spacing={1} sx={{ mt: 2 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<QrCodeScannerRoundedIcon />}
                          onClick={() => product.has_variants ? setVariantProduct(product) : setBarcodeProduct(product)}
                          sx={{ borderColor: "#bfdbfe" }}
                        >
                          {t(product.has_variants ? "products.manageVariants" : "products.manageBarcodes")}
                        </Button>
                        <Stack direction="row" spacing={1}>
                          <Button fullWidth variant="outlined" startIcon={<EditRoundedIcon />} onClick={() => openEditProduct(product)}>{t("common.edit")}</Button>
                          <Button fullWidth color="error" variant="outlined" startIcon={<DeleteOutlineRoundedIcon />} disabled={deletingId === product.public_id} onClick={() => remove(product)}>
                            {t(deletingId === product.public_id ? "products.deleting" : "common.delete")}
                          </Button>
                        </Stack>
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {pageCount > 1 && (
            <Stack sx={{ mt: 4, alignItems: "center" }} spacing={1}>
              <Pagination count={pageCount} page={page} onChange={(_, value) => setPage(value)} color="primary" shape="rounded" />
              <Typography variant="caption" color="text.secondary">
                {t("catalog.showing", { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, total), total: total.toLocaleString("en-US") })}
              </Typography>
            </Stack>
          )}
        </>
      )}

      <ProductBarcodeDialog
        open={Boolean(barcodeProduct)}
        product={barcodeProduct}
        onClose={() => setBarcodeProduct(null)}
      />
      <ProductVariantManager
        open={Boolean(variantProduct)}
        product={variantProduct}
        onClose={() => setVariantProduct(null)}
        onChanged={() => {
          invalidateWorkspaceCache(user?.public_id, selectedWorkspace?.public_id);
          load({ force: true });
        }}
      />

      <Dialog open={dialogOpen} onFocus={() => focusAIAction(editingProduct ? "products.edit" : "products.create")} onClose={() => { if (!saving) { setDialogOpen(false); setEditingProduct(null); setProductImageFile(null); setRemoveProductImage(false); } }} fullWidth maxWidth="sm">
        <DialogTitle fontWeight={900}>{t(editingProduct ? "products.edit" : "products.add")}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            {[
              ["name", t("catalog.name")],
              ["brand", t("catalog.brand")],
              ["category", t("catalog.category")],
              ["price", t("catalog.sellingPrice")],
              ["wholesale_price", t("products.wholesalePrice")],
              ["cost_price", t("catalog.catalogCost")],
              ["minimum_price", t("products.quotationMinimum")],
            ].map(([name, label]) => (
              <Grid item xs={12} sm={6} key={name}>
                {["brand", "category"].includes(name) ? (
                  <Autocomplete
                    freeSolo
                    options={name === "brand" ? brandSuggestions : categorySuggestions}
                    value={form[name]}
                    onInputChange={(_, value) => setForm((current) => ({ ...current, [name]: value }))}
                    onChange={(_, value) => setForm((current) => ({ ...current, [name]: value || "" }))}
                    renderInput={(params) => <TextField {...params} fullWidth required label={label} name={name} />}
                  />
                ) : (
                <TextField
                  fullWidth
                  required={["name", "brand", "category", "price"].includes(name)}
                  name={name}
                  label={label}
                  type="text"
                  autoComplete="off"
                  inputProps={MONEY_FIELDS.has(name)
                    ? {
                        inputMode: "decimal",
                        pattern: "[0-9]*[.]?[0-9]*",
                        min: 0,
                      }
                    : undefined}
                  value={form[name]}
                  onChange={(event) => {
                    const nextValue = MONEY_FIELDS.has(name)
                      ? normalizeMoneyInput(event.target.value)
                      : event.target.value;
                    setForm((current) => ({ ...current, [name]: nextValue }));
                  }}
                  slotProps={{ input: { sx: { color: "text.primary" } } }}
                />
                )}
              </Grid>
            ))}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label={t("products.wholesaleMinimum")}
                name="wholesale_min_quantity"
                value={form.wholesale_min_quantity}
                slotProps={{ htmlInput: { min: 1, step: 1 } }}
                onChange={(event) => setForm((current) => ({ ...current, wholesale_min_quantity: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={form.track_inventory} onChange={(event) => setForm((current) => ({ ...current, track_inventory: event.target.checked }))} />}
                label={t("products.trackInventory")}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {t("products.trackHelp")}
              </Typography>
            </Grid>
            {!editingProduct && !form.has_variants && form.track_inventory && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    name="initial_stock"
                    label={t("catalog.initialStock")}
                    type="number"
                    value={form.initial_stock}
                    onChange={(event) => setForm((current) => ({ ...current, initial_stock: event.target.value }))}
                    inputProps={{ min: 0, step: 1 }}
                    helperText={t("products.initialHelp")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    name="stock_minimum"
                    label={t("catalog.minimumStock")}
                    type="number"
                    value={form.stock_minimum}
                    onChange={(event) => setForm((current) => ({ ...current, stock_minimum: event.target.value }))}
                    inputProps={{ min: 0, step: 1 }}
                    helperText={t("products.minimumHelp")}
                  />
                </Grid>
              </>
            )}
            {!editingProduct && !form.has_variants && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="barcode"
                  label={t("catalog.barcodeOptional")}
                  placeholder={t("products.barcodePlaceholder")}
                  type="text"
                  autoComplete="off"
                  value={form.barcode}
                  onChange={(event) => setForm((current) => ({ ...current, barcode: event.target.value }))}
                  inputProps={{ maxLength: 160 }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <QrCodeScannerRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    },
                  }}
                  helperText={t("products.barcodeHelp")}
                />
              </Grid>
            )}
            {!editingProduct?.has_variants && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={form.has_variants} onChange={(event) => setForm((current) => ({ ...current, has_variants: event.target.checked, barcode: event.target.checked ? "" : current.barcode }))} />}
                  label={t(editingProduct ? "products.enableVariants" : "products.hasVariants")}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  {t("products.variantHelp")}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth label={t("catalog.description")} multiline minRows={3} value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <ProductImageField
                product={editingProduct}
                selectedFile={productImageFile}
                onSelectFile={setProductImageFile}
                removeExisting={removeProductImage}
                onRemoveExisting={setRemoveProductImage}
                disabled={saving}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => { setDialogOpen(false); setEditingProduct(null); setProductImageFile(null); setRemoveProductImage(false); }} disabled={saving}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={saveProduct} disabled={saving}>{saving ? <CircularProgress size={21} color="inherit" /> : t(editingProduct ? "catalog.saveChanges" : "catalog.create")}</Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
